import { useState, useRef } from "react";
import { useMsal } from "@azure/msal-react";
import { uploadToHold } from "../graphClient";

export default function DocumentsView({ onBack }) {
  const { instance, accounts } = useMsal();
  const fileInputRef = useRef(null);

  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  function handleFileSelect(e) {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setResult(null);
      setError(null);
    }
  }

  function handleRemoveFile() {
    setSelectedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }

  async function handleUpload() {
    if (!selectedFile) return;
    setUploading(true);
    setError(null);
    try {
      const { holdFilename } = await uploadToHold(
        instance,
        accounts[0],
        selectedFile
      );
      setResult(holdFilename);
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  }

  function handleUploadAnother() {
    setResult(null);
    setError(null);
    setSelectedFile(null);
  }

  function formatFileSize(bytes) {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={onBack}
          className="text-sm text-slate-500 hover:text-slate-700 px-2 py-1 rounded-lg hover:bg-slate-100 transition-colors"
        >
          &larr; Back
        </button>
        <h2 className="text-lg font-semibold text-slate-900 tracking-tight">
          Documents
        </h2>
      </div>

      {result && (
        <div className="bg-white rounded-2xl border border-green-200 p-6 text-center shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
          <p className="text-green-700 font-semibold text-lg mb-2">
            File uploaded
          </p>
          <p className="text-slate-800 text-sm font-mono bg-slate-100 rounded-lg px-3 py-2 mb-3 break-all">
            {result}
          </p>
          <p className="text-slate-500 text-sm mb-4">
            File received — will be classified and filed by Cowork
          </p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={handleUploadAnother}
              className="bg-[#1B3A5C] hover:bg-[#0E243B] text-white px-4 py-2.5 rounded-xl text-sm font-medium transition-colors"
            >
              Upload another
            </button>
            <button
              onClick={onBack}
              className="bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors"
            >
              Back to dashboard
            </button>
          </div>
        </div>
      )}

      {!result && (
        <>
          {error && (
            <div className="bg-white rounded-2xl border border-red-200 p-4 mb-4 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
              <p className="text-red-700 text-sm font-medium">Upload failed</p>
              <p className="text-red-500 text-xs mt-1 break-all">{error}</p>
            </div>
          )}

          <div className="bg-white rounded-2xl border border-slate-200/70 shadow-[0_1px_2px_rgba(15,23,42,0.04)] p-6">
            <p className="text-sm text-slate-500 mb-4">
              Select a file to upload to the HOLD folder. It will be classified
              and filed automatically.
            </p>

            <input
              ref={fileInputRef}
              type="file"
              onChange={handleFileSelect}
              className="hidden"
              id="file-upload"
            />

            {!selectedFile ? (
              <label
                htmlFor="file-upload"
                className="block w-full border-2 border-dashed border-slate-300 rounded-2xl p-8 text-center cursor-pointer hover:border-[#4A6D8C] hover:bg-slate-50 transition-colors"
              >
                <span className="text-3xl block mb-2">📄</span>
                <span className="text-[#1B3A5C] font-medium text-base">
                  Tap to select a file
                </span>
              </label>
            ) : (
              <div className="border border-slate-200 rounded-xl p-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-medium text-slate-900 text-sm truncate">
                      {selectedFile.name}
                    </p>
                    <p className="text-xs text-slate-400 mt-0.5 tabular-nums">
                      {formatFileSize(selectedFile.size)}
                    </p>
                  </div>
                  <button
                    onClick={handleRemoveFile}
                    className="text-slate-400 hover:text-red-500 text-sm shrink-0 px-2 py-1"
                  >
                    Remove
                  </button>
                </div>
              </div>
            )}

            {selectedFile && (
              <button
                onClick={handleUpload}
                disabled={uploading}
                className="w-full mt-4 bg-[#1B3A5C] hover:bg-[#0E243B] text-white py-3 rounded-xl font-medium text-base disabled:opacity-50 transition-colors"
              >
                {uploading ? "Uploading…" : "Upload to HOLD"}
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
